import { useCallback, useEffect, useMemo, useState } from "react";
import { fetchAdminProviders, updateAdminProviderStatus } from "../api/adminApi";
import { getAuthToken } from "../auth";
import { providers as mockProviders } from "../data";
import type { Provider } from "../types";

function mapApiProvider(row: {
  id: number;
  commercial_name: string;
  type: string;
  city: string;
  province: string;
  phone: string;
  email: string;
  status: "active" | "inactive";
}): Provider {
  const allowed: Provider["type"][] = [
    "HÔPITAL",
    "PHARMACIE",
    "CABINET MÉDICAL",
    "CLINIQUE",
    "LABORATOIRE",
  ];
  const type = allowed.includes(row.type as Provider["type"])
    ? (row.type as Provider["type"])
    : "HÔPITAL";

  return {
    id: String(row.id),
    commercialName: row.commercial_name,
    type,
    city: row.city || "—",
    province: row.province || "—",
    phone: row.phone || "—",
    email: row.email || "—",
    status: row.status,
  };
}

export function useAdminProviders() {
  const [providers, setProviders] = useState<Provider[]>(mockProviders);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [usingApi, setUsingApi] = useState(false);

  const load = useCallback(async () => {
    if (!getAuthToken()) {
      setUsingApi(false);
      setProviders(mockProviders);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);
    setUsingApi(true);

    try {
      const api = await fetchAdminProviders();
      setProviders(api.providers.map(mapApiProvider));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Impossible de charger les prestataires.");
      setProviders(mockProviders);
      setUsingApi(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const toggleStatus = useCallback(
    async (provider: Provider) => {
      if (!usingApi) return;
      const next = provider.status === "active" ? "inactive" : "active";
      const updated = await updateAdminProviderStatus(Number(provider.id), next);
      setProviders((current) =>
        current.map((row) => (row.id === provider.id ? mapApiProvider(updated) : row)),
      );
    },
    [usingApi],
  );

  const provinceOptions = useMemo(() => {
    const set = new Set<string>();
    for (const row of providers) {
      if (row.province && row.province !== "—") set.add(row.province);
    }
    return [
      { value: "", label: "Par province" },
      ...[...set].sort().map((value) => ({ value, label: value })),
    ];
  }, [providers]);

  return {
    providers,
    loading,
    error,
    usingApi,
    provinceOptions,
    reload: load,
    toggleStatus,
  };
}
